import React, { useRef, useEffect, useState } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PlaceResult {
  place_id: string;
  name: string;
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  rating?: number;
  price_level?: number;
  types: string[];
  photos?: google.maps.places.PlacePhoto[];
  website?: string;
}

interface PlacesAutocompleteProps {
  onPlaceSelect: (place: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  types?: string[];
  onGoogleApiLoaded?: () => void;
}

interface PlacesAutocompleteRef {
  initializeServices: () => void;
}

export const PlacesAutocomplete = React.forwardRef<PlacesAutocompleteRef, PlacesAutocompleteProps>(({
  onPlaceSelect,
  placeholder = "Search for restaurants...",
  className,
  types = ['restaurant', 'food', 'bar'],
  onGoogleApiLoaded
}, ref) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isApiLoaded, setIsApiLoaded] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  // Initialize Google Places services when API is loaded
  const initializeServices = React.useCallback(() => {
    if (window.google?.maps?.places && !isApiLoaded) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();

      // Create a dummy map div for PlacesService (required by Google API)
      const dummyMap = new window.google.maps.Map(document.createElement('div'));
      placesService.current = new window.google.maps.places.PlacesService(dummyMap);

      setIsApiLoaded(true);
      onGoogleApiLoaded?.();
    }
  }, [isApiLoaded, onGoogleApiLoaded]);

  // Check for existing API or wait for it to load
  useEffect(() => {
    if (window.google?.maps?.places) {
      initializeServices();
    }
  }, [initializeServices]);

  // Expose initialization function for external use
  React.useImperativeHandle(ref, () => ({
    initializeServices
  }));

  // Fetch suggestions from Google Places API
  const fetchSuggestions = React.useCallback((input: string) => {
    if (!autocompleteService.current || input.length < 2 || !isApiLoaded) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    const request: google.maps.places.AutocompletionRequest = {
      input,
      types,
      componentRestrictions: { country: 'us' }, // Restrict to US for better results
    };

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setIsLoading(false);

      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    });
  }, [types, isApiLoaded]);

  // Get place details when a suggestion is selected
  const getPlaceDetails = (placeId: string) => {
    if (!placesService.current) return;

    setIsLoading(true);

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId,
      fields: [
        'place_id',
        'name',
        'formatted_address',
        'geometry',
        'rating',
        'price_level',
        'types',
        'photos',
        'website'
      ]
    };

    placesService.current.getDetails(request, (place, status) => {
      setIsLoading(false);

      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const placeResult: PlaceResult = {
          place_id: place.place_id!,
          name: place.name!,
          formatted_address: place.formatted_address!,
          geometry: {
            location: {
              lat: place.geometry!.location!.lat(),
              lng: place.geometry!.location!.lng()
            }
          },
          rating: place.rating,
          price_level: place.price_level,
          types: place.types || [],
          photos: place.photos,
          website: place.website
        };

        onPlaceSelect(placeResult);
        setValue(place.name!);
        setShowSuggestions(false);
      }
    });
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (newValue.trim()) {
      fetchSuggestions(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    setSelectedIndex(-1);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (placeId: string) => {
    getPlaceDetails(placeId);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          // @ts-ignore
          handleSuggestionClick(suggestions[selectedIndex].place_id);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1">
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-5 w-5 text-muted-foreground z-10" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isApiLoaded ? placeholder : "Loading Google Maps..."}
          disabled={!isApiLoaded}
          className={cn(
            "pl-10 pr-10",
            className
          )}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 h-5 w-5 text-muted-foreground animate-spin z-10" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 z-50 mt-2 bg-background border rounded-sm shadow-sm max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion.place_id)}
              className={cn(
                "px-4 py-3 cursor-pointer border-b border-border/50 last:border-b-0 transition-colors",
                "hover:bg-muted",
                selectedIndex === index && "bg-muted"
              )}
            >
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {suggestion.structured_formatting.main_text}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {suggestion.structured_formatting.secondary_text}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default PlacesAutocomplete;